import { useState } from "react";
import { Button } from "@/shared/components/ui/button";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp, LayoutDashboard, LogOut } from "lucide-react";
import { trackClick } from "@/shared/utils/analytics";

interface NavItem {
  label: string;
  path?: string;
  sectionId?: string;
  external?: string;
  description?: string;
  subItems?: NavItem[];
}

interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
  onLogin: () => void;
  onLogout?: () => void;
  onJoinBeta?: () => void;
  navItems: NavItem[];
  onNavigate: (item: { path?: string; sectionId?: string; external?: string }) => void;
  isLoggedIn?: boolean;
  userEmail?: string | null;
}

export const MobileNav = ({
  isOpen,
  onClose,
  onLogin,
  onLogout,
  onJoinBeta,
  navItems,
  onNavigate,
  isLoggedIn = false,
  userEmail = null
}: MobileNavProps) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  const handleJoinBeta = () => {
    onClose();
    if (onJoinBeta) {
      onJoinBeta();
    }
  };

  const toggleExpanded = (index: number) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  if (!isOpen) return null;

  return (
    <div className="md:hidden bg-pivot-dark border-t border-pivot-gray-light">
      <div className="container mx-auto py-4 flex flex-col gap-2">
        {navItems.map((item, index) => (
          <div key={item.path || item.sectionId || index}>
            {/* Main menu item */}
            {item.subItems ? (
              // Item with submenu
              <button
                type="button"
                className={`w-full flex items-center justify-between text-white py-2 px-2 hover:text-pivot-primary transition-colors ${isRTL ? "text-right flex-row-reverse" : "text-left"}`}
                style={{ fontSize: "18px" }}
                onClick={() => toggleExpanded(index)}
              >
                <span>{item.label}</span>
                {expandedItems.has(index) ? (
                  <ChevronUp size={20} className="flex-shrink-0" />
                ) : (
                  <ChevronDown size={20} className="flex-shrink-0" />
                )}
              </button>
            ) : (
              // Regular item without submenu
              <button
                type="button"
                className={`w-full text-white py-2 px-2 hover:text-pivot-primary transition-colors ${isRTL ? "text-right" : "text-left"}`}
                style={{ fontSize: "18px" }}
                onClick={() => {
                  trackClick(item.label, "navbar");
                  onNavigate(item);
                  onClose();
                }}
              >
                {item.label}
              </button>
            )}

            {/* Submenu items */}
            {item.subItems && expandedItems.has(index) && (
              <div className={`mt-1 flex flex-col gap-1 border-pivot-primary/30 ${isRTL ? "mr-4 border-r-2 pr-4" : "ml-4 border-l-2 pl-4"}`}>
                {item.subItems.map((subItem, subIndex) => (
                  <button
                    key={subItem.path || subItem.sectionId || subIndex}
                    type="button"
                    className={`text-white/80 py-2 hover:text-pivot-primary transition-colors ${isRTL ? "text-right" : "text-left"}`}
                    style={{ fontSize: "15px" }}
                    onClick={() => {
                      trackClick(subItem.label, "navbar");
                      onNavigate(subItem);
                      onClose();
                    }}
                  >
                    {subItem.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
        <Link
          to="/privacy-policy"
          className="text-sm text-white py-2 hover:text-pivot-primary"
          onClick={onClose}
        >
          {t('footer.privacy')}
        </Link>
        <Link
          to="/terms-of-service"
          className="text-sm text-white py-2 hover:text-pivot-primary"
          onClick={onClose}
        >
          {t('footer.terms')}
        </Link>

        <div className="flex flex-col gap-2 pt-2">
          {isLoggedIn ? (
            <>
              {/* User Info */}
              <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg mb-2">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm text-black flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, #6dd8d0 0%, #4fc3b8 100%)' }}
                >
                  {userEmail ? userEmail[0].toUpperCase() : 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">
                    {userEmail ? userEmail.split('@')[0] : 'User'}
                  </p>
                  <p className="text-xs text-gray-400 truncate">
                    {userEmail || 'user@midash.ai'}
                  </p>
                </div>
              </div>

              {/* Go to Dashboard Button */}
              <Button
                className="w-full bg-pivot-primary text-black hover:bg-pivot-accent flex items-center justify-center gap-2"
                onClick={() => {
                  onClose();
                  navigate('/platform');
                }}
              >
                <LayoutDashboard size={18} />
                {t('nav.goToDashboard', 'Go to Dashboard')}
              </Button>

              {/* Logout Button */}
              <Button
                variant="outline"
                className="w-full hover:text-red-400 hover:border-red-400 flex items-center justify-center gap-2"
                onClick={() => {
                  onClose();
                  onLogout?.();
                }}
              >
                <LogOut size={18} />
                {t('settings.logout', 'Logout')}
              </Button>
            </>
          ) : (
            <>
              <Button
                className="w-full bg-pivot-primary text-black hover:bg-pivot-accent"
                onClick={handleJoinBeta}
              >
                {t('nav.joinBeta')}
              </Button>
              <Button
                variant="outline"
                className="w-full hover:text-pivot-primary hover:border-pivot-primary"
                onClick={() => {
                  trackClick("Login", "navbar");
                  onClose();
                  onLogin();
                }}
              >
                {t('common.login')}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
